<template>
  <div class="new-blog-container">
    <div class="new-blog-header">
      <h1>写编辑推荐</h1>
    </div>

    <div class="new-blog-content">
      <el-form 
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
        class="blog-form"
      >
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入编辑推荐标题" />
        </el-form-item>

        <el-form-item label="标签" prop="tagIds">
          <ContentTagSelect v-model="form.tagIds" scope="blog" />
        </el-form-item>

        <el-form-item label="分类" prop="categories">
          <el-select
            v-model="form.categories"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="请选择或输入分类"
          >
            <el-option
              v-for="category in availableCategories"
              :key="category.id"
              :label="category.category"
              :value="category.category"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="封面图" prop="banner">
          <el-upload
            class="banner-uploader"
            action="#"
            :show-file-list="false"
            :before-upload="beforeBannerUpload"
            :http-request="uploadBanner"
          >
            <img v-if="form.banner" :src="form.banner" class="banner" />
            <el-icon v-else class="banner-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="Twitter作者" prop="twitterAuthor">
          <el-input v-model="form.twitterAuthor" placeholder="请输入Twitter作者" />
        </el-form-item>

        <el-form-item label="作者" prop="authors">
          <el-select
            v-model="form.authors"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="请选择或输入作者"
          >
            <el-option
              v-for="author in availableAuthors"
              :key="author"
              :label="author"
              :value="author"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="摘要" prop="summary">
          <el-input
            v-model="form.summary"
            type="textarea"
            :rows="3"
            placeholder="请输入编辑推荐摘要"
          />
        </el-form-item>

        <el-form-item label="内容" prop="content">
          <MonoEdit v-model="form.content" height="min(75vh, 800px)" theme="vs-light" />
        </el-form-item>

        <el-form-item label="发布状态" prop="isPublished">
          <el-switch
            v-model="form.isPublished"
            :active-value="1"
            :inactive-value="0"
            active-text="发布"
            inactive-text="草稿"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="submitForm" :loading="loading">
            发布编辑推荐
          </el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Plus } from '@element-plus/icons-vue'
import MonoEdit from '@/components/MonoEdit.vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { validatePhotoUpload, appendAdminUploadScope, uploadResponseToUrl } from '@/utils/photoUpload'
import ContentTagSelect from '@/components/ContentTagSelect.vue'

export default {
  name: 'NewBlogView',
  components: {
    Plus,
    MonoEdit,
    ContentTagSelect
  },
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const loading = ref(false)
    const form = ref({
      title: '',
      tags: [],
      tagIds: [],
      categories: [],
      banner: '',
      twitterAuthor: '',
      authors: [],
      summary: '',
      content: '',
      isPublished: 0
    })

    const availableCategories = ref([])
    const availableAuthors = ref([])

    const rules = {
      title: [
        { required: true, message: '请输入标题', trigger: 'blur' },
        { min: 3, max: 100, message: '长度在 3 到 100 个字符', trigger: 'blur' }
      ],
      content: [
        { required: true, message: '请输入内容', trigger: 'blur' },
        { min: 10, message: '内容至少需要 10 个字符', trigger: 'blur' }
      ]
    }

    const fetchCategories = async () => {
      try {
        const response = await axios.post('/api/v1/blog/category/list', {
          page: 1,
          pageSize: 100
        })
        if (response.data.code === 200) {
          availableCategories.value = response.data.data.categories || []
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    const beforeBannerUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const uploadBanner = async (options) => {
      const { file, onSuccess, onError } = options
      const formData = new FormData()
      formData.append('file', file)
      appendAdminUploadScope(formData, 'blog')

      try {
        const response = await axios.post('/api/v1/upload', formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.code === 200) {
          form.value.banner = uploadResponseToUrl(response.data)
          onSuccess(response.data)
          ElMessage.success('图片上传成功')
        } else {
          onError(new Error(response.data.message || '上传失败'))
          ElMessage.error('图片上传失败')
        }
      } catch (error) {
        onError(error)
        ElMessage.error('图片上传失败，请重试')
      }
    }

    const submitForm = async () => {
      if (!formRef.value) return

      try {
        await formRef.value.validate()
        loading.value = true

        const token = localStorage.getItem('jwt_token')
        if (!token) {
          ElMessage.error('请先登录')
          return
        }

        const response = await axios.post('/api/v1/blog/create', form.value, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          ElMessage.success('编辑推荐发布成功')
          router.push('/blogs')
        } else {
          ElMessage.error(response.data.message || '编辑推荐发布失败')
        }
      } catch (error) {
        if (error.response) {
          // API error
          ElMessage.error(error.response.data.message || '编辑推荐发布失败')
        } else if (error.message) {
          // Validation error
          ElMessage.error(error.message)
        } else {
          // Other errors
          ElMessage.error('编辑推荐发布失败，请重试')
        }
        console.error('Error submitting form:', error)
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      if (formRef.value) {
        formRef.value.resetFields()
      }
    }

    onMounted(() => {
      fetchCategories()
    })

    return {
      formRef,
      form,
      rules,
      loading,
      availableCategories,
      availableAuthors,
      beforeBannerUpload,
      uploadBanner,
      submitForm,
      resetForm
    }
  }
}
</script>

<style scoped>
.new-blog-container {
  max-width: none;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.new-blog-header {
  margin-bottom: 30px;
}

.new-blog-header h1 {
  margin: 0;
  font-size: 28px;
  color: var(--el-text-color-primary);
}

.blog-form {
  max-width: none;
  width: 100%;
  margin: 0 auto;
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.banner-uploader {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.banner-uploader:hover {
  border-color: var(--el-color-primary);
}

.banner-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
  line-height: 178px;
}

.banner {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

@media (max-width: 768px) {
  .new-blog-container {
    padding: 10px;
  }

  .blog-form {
    padding: 15px;
  }
}
</style> 